import { useEffect, useId, useState } from 'react';
import type { AnnonceDetail, LienFormulaire } from '@rh/shared';
import { appel } from '../api';
import { Erreur } from './Etat';
import { CarteLienFormulaire } from './LienFormulaire';

/** Ce que les écrans lisent d'un référentiel : de quoi remplir une liste. */
interface Referentiel {
  id: string;
  libelle: string;
}

/**
 * « Nouvelle annonce RS » (docs/FORMULAIRE_PUBLIC.md § 8) : un poste publié
 * seulement sur les réseaux sociaux.
 *
 * Deux champs suffisent — titre et métier. Pas de texte long, pas de critères,
 * pas de publications : l'annonce naît validée, avec sa référence, son barème
 * figé et son lien, en une seule fois. C'est la différence avec « Nouvelle
 * annonce », qui ouvre le formulaire guidé complet.
 *
 * À la validation, la fenêtre ne se ferme pas : elle montre l'écran « Lien
 * prêt ». C'est le moment où l'utilisateur en a besoin — il va le coller dans
 * sa publication — et le refermer pour aller le rechercher serait absurde.
 */
export function NouvelleAnnonceRs({ surCreation }: { surCreation: () => void }) {
  const [ouvert, setOuvert] = useState(false);
  const [familles, setFamilles] = useState<Referentiel[] | null>(null);
  const [points, setPoints] = useState<Referentiel[] | null>(null);
  const [intitule, setIntitule] = useState('');
  const [familleId, setFamilleId] = useState('');
  const [choisis, setChoisis] = useState<string[]>([]);
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const [cree, setCree] = useState<{ annonce: AnnonceDetail; lien: LienFormulaire } | null>(null);
  const titre = useId();

  useEffect(() => {
    if (!ouvert || familles) return;
    // Les référentiels ne rendent que les entrées actives sans `?inactives`.
    appel<Referentiel[]>('/familles')
      .then(setFamilles)
      .catch(() => setFamilles([]));
    appel<Referentiel[]>('/points-de-vente')
      .then(setPoints)
      .catch(() => setPoints([]));
  }, [ouvert, familles]);

  function fermer(): void {
    setOuvert(false);
    if (cree) surCreation();
    setCree(null);
    setIntitule('');
    setFamilleId('');
    setChoisis([]);
    setErreur(null);
  }

  async function creer(): Promise<void> {
    setEnvoi(true);
    setErreur(null);
    try {
      setCree(
        await appel<{ annonce: AnnonceDetail; lien: LienFormulaire }>('/annonces/rs', {
          method: 'POST',
          body: JSON.stringify({ intitule: intitule.trim(), familleId, pointsDeVenteIds: choisis }),
        }),
      );
    } catch (e) {
      setErreur(e instanceof Error ? e.message : 'Création impossible');
    } finally {
      setEnvoi(false);
    }
  }

  return (
    <>
      <button type="button" className="bouton-second" onClick={() => setOuvert(true)}>
        Nouvelle annonce RS
      </button>

      {ouvert && (
        <div className="dialogue-fond">
          <dialog open className="dialogue dialogue-rs" aria-modal="true" aria-labelledby={titre}>
            {cree ? (
              <>
                <h2 id={titre}>Lien prêt</h2>
                <p className="mention">
                  « {cree.annonce.intitule} » est publiée sous la référence{' '}
                  <code>{cree.annonce.reference}</code>. Collez ce lien dans votre publication : les
                  candidatures arriveront dans la liste de cette annonce.
                </p>
                <CarteLienFormulaire
                  lien={cree.lien}
                  rh
                  surChangement={(lien) => setCree({ ...cree, lien })}
                />
                <div className="actions">
                  <button type="button" className="bouton" onClick={fermer}>
                    Terminé
                  </button>
                </div>
              </>
            ) : (
              <>
                <h2 id={titre}>Nouvelle annonce sur les réseaux sociaux</h2>
                <p className="mention">
                  Un titre et un métier suffisent. L’annonce est publiée aussitôt avec son lien de
                  formulaire, sans texte ni critères — l’adéquation automatique restera donc muette
                  tant que vous ne l’aurez pas complétée par « Texte et publications ».
                </p>

                <label className="champ">
                  <span>Intitulé du poste</span>
                  <input
                    type="text"
                    value={intitule}
                    maxLength={120}
                    disabled={envoi}
                    placeholder="ex. Vendeuse en pâtisserie"
                    onChange={(e) => setIntitule(e.target.value)}
                  />
                </label>

                <label className="champ">
                  <span>Métier</span>
                  <select
                    value={familleId}
                    disabled={!familles || envoi}
                    onChange={(e) => setFamilleId(e.target.value)}
                  >
                    <option value="">choisir un métier…</option>
                    {(familles ?? []).map((famille) => (
                      <option key={famille.id} value={famille.id}>
                        {famille.libelle}
                      </option>
                    ))}
                  </select>
                </label>

                {points && points.length > 0 && (
                  <fieldset className="champ points-de-vente">
                    <legend>Points de vente (facultatif)</legend>
                    {points.map((point) => (
                      <label key={point.id} className="case">
                        <input
                          type="checkbox"
                          checked={choisis.includes(point.id)}
                          disabled={envoi}
                          onChange={(e) =>
                            setChoisis((actuels) =>
                              e.target.checked
                                ? [...actuels, point.id]
                                : actuels.filter((id) => id !== point.id),
                            )
                          }
                        />
                        <span>{point.libelle}</span>
                      </label>
                    ))}
                  </fieldset>
                )}

                {erreur && <Erreur message={erreur} />}

                <div className="actions">
                  <button
                    type="button"
                    className="bouton"
                    disabled={envoi || intitule.trim().length < 3 || !familleId}
                    onClick={() => void creer()}
                  >
                    {envoi ? 'Création…' : 'Valider et créer le lien'}
                  </button>
                  <button type="button" className="lien" disabled={envoi} onClick={fermer}>
                    Annuler
                  </button>
                </div>
              </>
            )}
          </dialog>
        </div>
      )}
    </>
  );
}
